// Segment logic for the Shared Media (My Brand) media-type control: which of
// Images / Video is shown, which side is locked and why, and the caption that
// explains it. Pure functions so the rules are unit-testable (MB-1492).

// Platforms that only accept video / only accept images. MediaPickerDialog
// imports these so the segment logic and the tab constraints cannot drift.
export const VIDEO_ONLY_PLATFORMS = ["youtube"];
export const IMAGE_ONLY_PLATFORMS = ["pinterest", "google", "gmb"];

export type SharedMediaSegment = "images" | "video";

// "youtube" / "video-post" / "image-only-platforms" are platform-level locks
// (the segment cannot be used for this post at all); "selection" means the
// already-picked media of the other type is the only thing in the way.
export type SegmentLockReason =
    | "youtube"
    | "video-post"
    | "image-only-platforms"
    | "selection"
    | null;

export interface SegmentLocks {
    images: SegmentLockReason;
    video: SegmentLockReason;
}

export interface SharedSegmentInput {
    platforms: string[];
    isVideo: boolean;
    hasVideoSelected: boolean;
    hasImageSelected: boolean;
}

export function sharedSegmentLocks({
    platforms,
    isVideo,
    hasVideoSelected,
    hasImageSelected,
}: SharedSegmentInput): SegmentLocks {
    const hasYoutube = platforms.some((p) => VIDEO_ONLY_PLATFORMS.includes(p));
    const hasImageOnly = platforms.some((p) => IMAGE_ONLY_PLATFORMS.includes(p));
    return {
        images: hasYoutube
            ? "youtube"
            : isVideo
            ? "video-post"
            : hasVideoSelected
            ? "selection"
            : null,
        video: hasImageOnly
            ? "image-only-platforms"
            : hasImageSelected
            ? "selection"
            : null,
    };
}

// Segment shown on entry to the step. Platform locks win; media already picked
// keeps its segment; an explicit choice made earlier in this wizard survives;
// otherwise default to Video - every platform left standing after the locks
// supports video posts, and most My Brand posts are video.
export function deriveSharedMediaSegment(
    input: SharedSegmentInput & { priorChoice?: string }
): SharedMediaSegment {
    const locks = sharedSegmentLocks(input);
    if (locks.images === "youtube" || locks.images === "video-post") return "video";
    if (locks.video === "image-only-platforms") return "images";
    if (input.hasVideoSelected) return "video";
    if (input.hasImageSelected) return "images";
    if (input.priorChoice === "image") return "images";
    if (input.priorChoice === "video") return "video";
    return "video";
}

// On the Video side of the control a video is only mandatory when a selected
// platform actually requires media - YouTube (video required) or any platform
// with a minimum media count (Instagram, Pinterest, Google). A FB / X /
// LinkedIn-only text post stays legal from the Video side too.
export function videoSideRequiresMedia(
    platforms: string[],
    platformMins: number[]
): boolean {
    return (
        platforms.some((p) => VIDEO_ONLY_PLATFORMS.includes(p)) ||
        platformMins.some((min) => min > 0)
    );
}

const IMAGE_ONLY_LABELS: Record<string, string> = {
    pinterest: "Pinterest",
    google: "Google My Business",
    gmb: "Google My Business",
};

export function imageOnlyPlatformLabel(platforms: string[]): {
    label: string;
    plural: boolean;
} {
    const names: string[] = [];
    platforms.forEach((p) => {
        const label = IMAGE_ONLY_LABELS[p];
        if (label && !names.includes(label)) names.push(label);
    });
    return { label: names.join(" and "), plural: names.length > 1 };
}

// One-line explanation under the control. Platform locks explain the post,
// selection locks explain the way out, and the video-first default announces
// itself once so nobody hunts for image upload.
export function sharedSegmentCaption(args: {
    locks: SegmentLocks;
    platforms: string[];
    segment: SharedMediaSegment;
    heuristicDefaultActive: boolean;
}): { lead: string; rest: string } | null {
    const { locks, platforms, segment, heuristicDefaultActive } = args;
    if (locks.images === "youtube") {
        return {
            lead: "YouTube posts require a video",
            rest: "image posts are unavailable while YouTube is selected.",
        };
    }
    if (locks.images === "video-post") {
        return {
            lead: "This post requires a video",
            rest: "image posts are unavailable here.",
        };
    }
    if (locks.video === "image-only-platforms") {
        const { label, plural } = imageOnlyPlatformLabel(platforms);
        return {
            lead: "Video is unavailable",
            rest: `${label} support${plural ? "" : "s"} image posts only.`,
        };
    }
    if (locks.images === "selection") {
        return {
            lead: "A video is selected",
            rest: "remove it to switch back to Images.",
        };
    }
    if (locks.video === "selection") {
        return {
            lead: "Images are selected",
            rest: "remove them to switch to Video.",
        };
    }
    if (segment === "video" && heuristicDefaultActive) {
        return {
            lead: "Video selected for you",
            rest: "all of your selected platforms support video posts. Switch to Images anytime.",
        };
    }
    return null;
}

// Toast for a click on a locked segment - guard the click and say why,
// never a silent no-op.
export function segmentLockMessage(
    segment: SharedMediaSegment,
    reason: Exclude<SegmentLockReason, null>,
    platforms: string[]
): string {
    if (segment === "images") {
        if (reason === "youtube") {
            return "Images are unavailable while YouTube is selected. Deselect YouTube to post images.";
        }
        if (reason === "video-post") {
            return "Images are unavailable for a video post.";
        }
        return "Remove the selected video before switching to images.";
    }
    if (reason === "image-only-platforms") {
        const { label, plural } = imageOnlyPlatformLabel(platforms);
        return `${label} support${plural ? "" : "s"} images only. Deselect ${
            plural ? "them" : "it"
        } to post a video.`;
    }
    return "Remove the selected images before switching to a video.";
}

// ── Library sources under the segmented control (MB-1496) ──
// Each segment offers the sources that make sense for it; a source maps onto
// the dialog's existing mediaType / resource-type axes so all plumbing below
// the chips is unchanged.
export interface SegmentSource {
    key: "listing" | "shared" | "logos" | "archived";
    label: string;
    mediaType: "image" | "video" | "assets" | "archived";
    resourceType: "Image" | "Video" | "Logo" | null;
}

export interface SegmentSourceInput {
    sharedMediaOnly: boolean;
    hasListingImages: boolean;
    hasListingVideos: boolean;
    hasLogos: boolean;
    hasArchived: boolean;
}

export function segmentSources(
    segment: SharedMediaSegment,
    input: SegmentSourceInput
): SegmentSource[] {
    if (input.sharedMediaOnly) {
        // My Brand: shared media is the only library - the chips split it
        // into photos and logos on the image side; the video side is implicit.
        if (segment === "video") return [];
        return [
            { key: "shared", label: "Photos", mediaType: "assets", resourceType: "Image" },
            { key: "logos", label: "Logos", mediaType: "assets", resourceType: "Logo" },
        ];
    }
    if (segment === "video") {
        const sources: SegmentSource[] = [];
        if (input.hasListingVideos) {
            sources.push({ key: "listing", label: "Listing", mediaType: "video", resourceType: null });
        }
        // Shared is always offered - it is where uploads land.
        sources.push({ key: "shared", label: "Shared", mediaType: "assets", resourceType: "Video" });
        return sources;
    }
    const sources: SegmentSource[] = [];
    if (input.hasListingImages) {
        sources.push({ key: "listing", label: "Listing", mediaType: "image", resourceType: null });
    }
    // Shared is always offered - it is where uploads land, so the post-upload
    // reveal always has a source to switch to.
    sources.push({ key: "shared", label: "Shared", mediaType: "assets", resourceType: "Image" });
    if (input.hasLogos) {
        sources.push({ key: "logos", label: "Logos", mediaType: "assets", resourceType: "Logo" });
    }
    if (input.hasArchived) {
        sources.push({ key: "archived", label: "Archived", mediaType: "archived", resourceType: null });
    }
    return sources;
}
